import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/payment_configuration';

// The only two methods that can ever be persisted: a Stripe card or a Twikey
// SEPA mandate. `null` means none is set yet. Narrowed from the legacy
// `z.string().nullable()` read (audit rule) — the backend only ever writes
// these two constants.
const PaymentMethodSchema = z.enum(['stripe', 'twikey']);

// Card fields are server-managed mirrors of the Stripe customer: always present
// as keys, `null` until a card is on file.
const PaymentCardInformationSchema = z.object({
  canEditCard: z.boolean(),
  last4Digits: z.string().nullable(),
  expiryMonth: z.string().nullable(),
  expiryYear: z.string().nullable(),
  holderName: z.string().nullable(),
  cardBrand: z.string().nullable(),
});

export const PaymentConfigurationSchema = z.object({
  paymentMethod: PaymentMethodSchema.nullable(),
  cardInformation: PaymentCardInformationSchema,
  directBillingByIntegrator: z.boolean(),
});

export type PaymentConfiguration = z.infer<typeof PaymentConfigurationSchema>;

export async function loadPaymentConfigurationQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = PaymentConfigurationSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'paymentConfiguration');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadPaymentConfiguration(options: QueryOptions<PaymentConfiguration> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<PaymentConfiguration, ApiError>({
    queryKey: ['paymentConfiguration'],
    queryFn: () => loadPaymentConfigurationQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<PaymentConfiguration, ApiError, 'paymentConfiguration'>(
    'paymentConfiguration',
    queryResult,
  );
}
